// FLAGS: none
// The "Autofill with AI" button plus the note that tells the expert how long it will keep working.
// Transcripts are deleted 24 hours after a session; without this the button just disappears.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import Button from "@openup/shared/components/Button/Button";
import { Clock, Hourglass } from "lucide-react";
import clsx from "clsx";
import { RESPONSE_STATUS } from "@src/constants";
import { ResponseConsultsConsultWithNotesResponse } from "@src/api/v1/data-contracts";
import getAutofillAvailability from "@src/views/ClientPage/SessionNotes/autofillAvailability";

const CAPTION_ID = "autofill-with-ai-availability";

interface AutofillWithAiProps {
  consult: Pick<
    ResponseConsultsConsultWithNotesResponse,
    "transcriptAvailableUntil" | "transcriptionDeletedOn"
  >;
  isPending: boolean;
  error: Error | null;
  onAutofill: () => void;
}

// The window closes while the page can stay open, so the state is re-derived every minute.
const useMinuteClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

// "in 2 hours" / "in 105 minutes", rounded down so the expert is never promised more time than is left.
// Under two hours it counts minutes, so "1 hour" never stands for 1 h 59 min.
const formatTimeLeft = (minutesLeft: number, language: string) => {
  const relative = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  return minutesLeft >= 120
    ? relative.format(Math.floor(minutesLeft / 60), "hour")
    : relative.format(minutesLeft, "minute");
};

const AutofillWithAi: React.FC<AutofillWithAiProps> = ({
  consult,
  isPending,
  error,
  onAutofill,
}) => {
  const { t, i18n } = useTranslation();
  const now = useMinuteClock();
  const transcriptGone =
    (error as AxiosError | null)?.response?.status ===
    RESPONSE_STATUS.NOT_FOUND;
  const availability = getAutofillAvailability(consult, now, transcriptGone);

  if (availability.state === "none") return null;

  if (availability.state === "expired") {
    return (
      <output className="flex items-start gap-3 p-4 mb-2 max-w-xs text-left bg-slate-100 rounded-md border border-solid border-slate-200">
        <Clock className="text-slate-600 shrink-0" size={18} />
        <span className="flex flex-col">
          <span className="text-sm font-medium pb-1 text-slate-800">
            {t("SessionNotesPaper.Autofill.ExpiredTitle")}
          </span>
          <span className="text-xs font-medium text-slate-600">
            {t("SessionNotesPaper.Autofill.ExpiredBody")}
          </span>
        </span>
      </output>
    );
  }

  const { deadline } = availability;
  const language = i18n.resolvedLanguage ?? i18n.language;
  const isExpiring = availability.state === "expiring";
  const minutesLeft = Math.floor((deadline.getTime() - now.getTime()) / 60_000);

  // 24-hour times, like the session details on the same page.
  const getCaption = () => {
    if (!isExpiring) {
      return t("SessionNotesPaper.Autofill.AvailableUntil", {
        deadline: new Intl.DateTimeFormat(language, {
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        }).format(deadline),
      });
    }
    return t("SessionNotesPaper.Autofill.ExpiresIn", {
      relative: formatTimeLeft(minutesLeft, language),
      time: new Intl.DateTimeFormat(language, {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(deadline),
    });
  };
  const CaptionIcon = isExpiring ? Hourglass : Clock;

  return (
    <div className="flex flex-col items-start gap-1.5 p-2 max-w-xs">
      <Button
        loading={isPending}
        onClick={onAutofill}
        aria-describedby={CAPTION_ID}
        className="!px-4 !py-1"
      >
        {t("SessionNotesPaper.Autofill.Button")}
      </Button>
      <p
        id={CAPTION_ID}
        className={clsx(
          "flex items-start gap-1.5 text-xs",
          isExpiring ? "font-medium text-amber-700" : "text-slate-600",
        )}
      >
        <CaptionIcon aria-hidden="true" className="mt-px shrink-0" size={14} />
        <span>{getCaption()}</span>
      </p>
      {error && (
        <p className="text-xs text-red-500" role="alert">
          {t("Common.GenericErrorMessage")}
        </p>
      )}
    </div>
  );
};

export default AutofillWithAi;
